import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from "@angular/core";
import { COMMANDE_CLIENT_LIST_DATA } from "../../models/data.model";
import { Widget, WidgetSubType } from "../../models/dashboard.model";
import { DatePipe, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { CommandeClientEtat } from "../../../commande-client/enums/etat-commande-client.enum";
import { CommandeClient } from "../../../commande-client/models/commande-client.model";
import { Router } from "@angular/router";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { CommandePilotageDialogComponent } from "../commande-pilotage-dialog/commande-pilotage-dialog.component";
import {
  CommandePilotageDialogData,
  CommandePilotageItem,
} from "../commande-pilotage-dialog/commande-pilotage.types";

const ETAT_LABEL_CLIENT: Record<string, string> = {
  [CommandeClientEtat.EN_COURS_DE_TRAITEMENT]: "À traiter",
  [CommandeClientEtat.EN_COURS_DE_PREPARATION]: "À préparer",
};

const ETAT_ORDER_CLIENT: Record<string, number> = {
  [CommandeClientEtat.EN_COURS_DE_TRAITEMENT]: 1,
  [CommandeClientEtat.EN_COURS_DE_PREPARATION]: 2,
};

interface EnrichedCommandeClient {
  commande: CommandeClient;
  nbLignes: number;
  isOverdue: boolean;
  daysLate: number;
}

@Component({
  selector: "vl-widget-commande-client-list",
  standalone: true,
  imports: [
    SharedModule,
    DatePipe,
    DecimalPipe,
    ChipsComponent,
    WidgetTitleComponent,
    MatIcon,
    MatTooltip,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-commande-client-list.component.html",
  styleUrl: "./widget-commande-client-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCommandeClientListComponent {
  data = input<COMMANDE_CLIENT_LIST_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  /** Onglet sélectionné (null tant qu'auto-sélection non appliquée). */
  subType = signal<WidgetSubType | null>(null);
  widgetSubType = WidgetSubType;
  router = inject(Router);
  private dialogs = inject(DialogsService);

  /** L'utilisateur a choisi un onglet manuellement → on ne re-sélectionne plus
   *  automatiquement à chaque rechargement. */
  private userPickedTab = signal<boolean>(false);

  /** Phase 4 : "Expédition partielle" filtre les CC en EN_COURS_DE_PREPARATION
   *  avec etatExpedition = PARTIELLEMENT (livraisons commencées mais pas finies).
   *  Quand etatExpedition = TOTAL, la CC bascule auto en TERMINE et n'apparaît
   *  plus dans le widget. */
  private isPartiallyShipped(c: any): boolean {
    return (
      c?.etat === CommandeClientEtat.EN_COURS_DE_PREPARATION &&
      (c?.etatExpedition ?? "NON_EXPEDIE") === "PARTIELLEMENT"
    );
  }

  private countByEtat(etat: CommandeClientEtat): number {
    const list = this.data()?.commandesClient ?? [];
    if (etat === CommandeClientEtat.EN_COURS_DE_PREPARATION) {
      // Onglet "À préparer" — exclut les CC partiellement livrées (elles vont
      // dans l'onglet "Expédition partielle").
      return list.filter(
        (c) => c.etat === etat && !this.isPartiallyShipped(c),
      ).length;
    }
    return list.filter((c) => c.etat === etat).length;
  }

  countTraitement = computed(() =>
    this.countByEtat(CommandeClientEtat.EN_COURS_DE_TRAITEMENT),
  );
  countPreparation = computed(() =>
    this.countByEtat(CommandeClientEtat.EN_COURS_DE_PREPARATION),
  );
  /** Phase 4 : compte les CC en préparation avec expédition partielle. */
  countPartielle = computed(
    () =>
      (this.data()?.commandesClient ?? []).filter((c) =>
        this.isPartiallyShipped(c),
      ).length,
  );

  /** Total brut affiché à côté du titre. */
  totalGlobal = computed(
    () =>
      this.countTraitement() + this.countPreparation() + this.countPartielle(),
  );

  /** Liste enrichie, filtrée sur l'onglet courant, triée par urgence. */
  commandesClient = computed<EnrichedCommandeClient[]>(() => {
    const sub = this.subType();
    if (!this.data() || !sub) return [];
    const today = startOfDay(new Date());
    // Cas spécial Phase 4 : l'onglet "Expédition partielle" filtre par
    // `etatExpedition = PARTIELLEMENT`, pas par `etat`.
    if (sub === WidgetSubType.COMMANDE_CLIENT_PRETE) {
      return (this.data()?.commandesClient ?? [])
        .filter((c) => this.isPartiallyShipped(c))
        .map((c) => this.enrich(c, today))
        .sort((a, b) => {
          if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
          if (a.isOverdue && b.isOverdue) return b.daysLate - a.daysLate;
          const da = a.commande.dateLivraisonPrevue
            ? new Date(a.commande.dateLivraisonPrevue).getTime()
            : Infinity;
          const db = b.commande.dateLivraisonPrevue
            ? new Date(b.commande.dateLivraisonPrevue).getTime()
            : Infinity;
          return da - db;
        });
    }
    const etat = this.etatForSubType(sub);
    if (!etat) return [];
    return (this.data()?.commandesClient ?? [])
      .filter((c) => {
        if (c.etat !== etat) return false;
        // Pour l'onglet "À préparer" : exclure les CC partiellement livrées
        // (elles apparaissent dans l'onglet "Expédition partielle").
        if (
          etat === CommandeClientEtat.EN_COURS_DE_PREPARATION &&
          this.isPartiallyShipped(c)
        ) {
          return false;
        }
        return true;
      })
      .map((c) => this.enrich(c, today))
      .sort((a, b) => {
        if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
        if (a.isOverdue && b.isOverdue) return b.daysLate - a.daysLate;
        const da = a.commande.dateLivraisonPrevue
          ? new Date(a.commande.dateLivraisonPrevue).getTime()
          : Infinity;
        const db = b.commande.dateLivraisonPrevue
          ? new Date(b.commande.dateLivraisonPrevue).getTime()
          : Infinity;
        return da - db;
      });
  });

  /** Synthèse de l'onglet courant : nb en retard. */
  filteredStats = computed(() => {
    const list = this.commandesClient();
    let overdue = 0;
    for (const e of list) if (e.isOverdue) overdue++;
    return { overdue, total: list.length };
  });

  constructor() {
    // Auto-sélection : premier onglet non-vide à l'arrivée des données
    // (ordre métier : Traitement → Préparation → Prête).
    effect(
      () => {
        const data = this.data();
        if (!data) return;
        if (this.userPickedTab()) return;
        untracked(() => {
          const order: { sub: WidgetSubType; count: number }[] = [
            {
              sub: WidgetSubType.COMMANDE_CLIENT_EN_COURS_DE_TRAITEMENT,
              count: this.countTraitement(),
            },
            {
              sub: WidgetSubType.COMMANDE_CLIENT_EN_COURS_DE_PREPARATION,
              count: this.countPreparation(),
            },
            {
              sub: WidgetSubType.COMMANDE_CLIENT_PRETE,
              count: this.countPartielle(),
            },
          ];
          const firstWithItems = order.find((o) => o.count > 0);
          this.subType.set(
            firstWithItems
              ? firstWithItems.sub
              : WidgetSubType.COMMANDE_CLIENT_EN_COURS_DE_TRAITEMENT,
          );
        });
      },
      { allowSignalWrites: true },
    );
  }

  selectTab(sub: WidgetSubType): void {
    this.userPickedTab.set(true);
    this.subType.set(sub);
  }

  onSelect(elem: EnrichedCommandeClient): void {
    this.router.navigateByUrl(
      `/commandeclient/details/id/${elem.commande.id}/commande-client`,
    );
  }

  /** Ouvre le dialog de pilotage avec KPIs, breakdown par étape/tiers et
   *  export Excel multi-feuilles. Limité aux 2 états "en cours" du widget
   *  (À traiter / À préparer) — on n'inclut pas TERMINE, ANNULEE, etc. qui
   *  sont des états terminaux. */
  goToList(): void {
    const today = startOfDay(new Date());
    const items: CommandePilotageItem[] = (
      this.data()?.commandesClient ?? []
    )
      .filter((c) => ETAT_LABEL_CLIENT[c.etat] !== undefined)
      .map((c) => {
      const datePrev = c.dateLivraisonPrevue
        ? startOfDay(new Date(c.dateLivraisonPrevue))
        : null;
      const isOverdue =
        datePrev !== null && datePrev.getTime() < today.getTime();
      const daysLate =
        isOverdue && datePrev
          ? Math.floor(
              (today.getTime() - datePrev.getTime()) / (1000 * 60 * 60 * 24),
            )
          : 0;
      return {
        id: c.id,
        numero: c.numero ?? "",
        etat: c.etat,
        etatLabel: ETAT_LABEL_CLIENT[c.etat] ?? c.etat,
        etatOrder: ETAT_ORDER_CLIENT[c.etat] ?? 99,
        etatExpedition: (c as any).etatExpedition ?? null,
        etatFacturation: (c as any).etatFacturation ?? null,
        tiersId: c.tiers?.id ?? null,
        tiersDenomination: c.tiers?.denomination ?? "—",
        dateCreation: (c as any).dateCreation ?? null,
        dateLivraisonPrevue: c.dateLivraisonPrevue ?? null,
        totalHT: (c as any).totalHT ?? 0,
        totalTTC: (c as any).totalTTC ?? 0,
        nbLignes: (c as any).nbLignes ?? c.lignes?.length ?? 0,
        daysLate,
      };
    });
    const dialogData: CommandePilotageDialogData = {
      mode: "client",
      items,
    };
    this.dialogs.open(
      CommandePilotageDialogComponent,
      "min(95vw, 1200px)",
      "min(90vh, 800px)",
      dialogData,
    );
  }

  private etatForSubType(sub: WidgetSubType): CommandeClientEtat | null {
    switch (sub) {
      case WidgetSubType.COMMANDE_CLIENT_EN_COURS_DE_TRAITEMENT:
        return CommandeClientEtat.EN_COURS_DE_TRAITEMENT;
      case WidgetSubType.COMMANDE_CLIENT_EN_COURS_DE_PREPARATION:
        return CommandeClientEtat.EN_COURS_DE_PREPARATION;
      default:
        // COMMANDE_CLIENT_PRETE → géré séparément via isPartiallyShipped()
        return null;
    }
  }

  private enrich(c: CommandeClient, today: Date): EnrichedCommandeClient {
    const datePrev = c.dateLivraisonPrevue
      ? startOfDay(new Date(c.dateLivraisonPrevue))
      : null;
    const isOverdue = datePrev !== null && datePrev.getTime() < today.getTime();
    const daysLate =
      isOverdue && datePrev
        ? Math.floor((today.getTime() - datePrev.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    return {
      commande: c,
      nbLignes: c.lignes?.length ?? 0,
      isOverdue,
      daysLate,
    };
  }
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
